import type { BinGroup as BinGroupType, ExtendedBin, Gondola } from "./types";
import { useViewSettings } from "@/view-settings";
import { useObservable, useSelector } from "@legendapp/state/react";
import { useEffect, useState } from "react";
import { BinGroup } from "./bin-group";
import { SelectedBin } from "./selected-bin";
import { calcBinDimensions } from "./utils";
import { selectedBin$ } from "@/virtual-store/state";
import { PlanogramResponse } from "@/shared/api";

export function Bins({ planogram }: { planogram: PlanogramResponse }) {
  const viewSettings = useViewSettings();
  const isVisible = useSelector(() => viewSettings.showBins.get());
  const hovered$ = useObservable<number | null>(null);

  useEffect(() => {
    if (!planogram) return;

    const navigateLeft = (bin: ExtendedBin): ExtendedBin | null => {
      let { gondolaIndex, shelfIndex, binIndex } = bin;

      let gondola = planogram?.result.gondolas[gondolaIndex];
      let shelf = gondola?.shelves[shelfIndex];
      let shelfLength = gondola?.shelves.length || 0;
      let binsLength = shelf?.bins.length || 0;

      if (binIndex === 0 && shelfIndex === 0 && gondolaIndex === 0) {
        gondolaIndex = 0;
        shelfIndex = 0;
        binIndex = 0;
      } else if (binIndex === 0 && shelfIndex > 0 && gondolaIndex > 0) {
        shelfIndex = shelfIndex - 1;
        shelf = gondola?.shelves[shelfIndex];
        binsLength = shelf?.bins.length || 0;

        binIndex = binsLength;
      } else if (binIndex === 0 && shelfIndex === 0 && gondolaIndex > 0) {
        gondolaIndex = gondolaIndex - 1;
        gondola = planogram?.result.gondolas[gondolaIndex];
        shelfLength = gondola?.shelves.length || 0;

        shelfIndex = shelfLength - 1;
        shelf = gondola?.shelves[shelfIndex];
        binsLength = shelf?.bins.length || 0;

        binIndex = binsLength;
      }

      binIndex = binIndex - 1;

      const newBin =
        planogram?.result.gondolas[gondolaIndex].shelves[shelfIndex].bins[
          binIndex
        ];

      if (!newBin) return null;

      return {
        ...newBin,
        gondolaIndex,
        shelfIndex,
        binIndex,
      } as ExtendedBin;
    };

    const navigateRight = (bin: ExtendedBin): ExtendedBin | null => {
      let { gondolaIndex, shelfIndex, binIndex } = bin;

      const gondolas = planogram?.result.gondolas || [];
      const gondola = planogram?.result.gondolas[gondolaIndex];
      const shelf = gondola?.shelves[shelfIndex];
      const binsLength = shelf?.bins.length || 0;
      const shelfLength = gondola?.shelves.length || 0;

      if (binIndex === binsLength - 1) {
        if (shelfIndex === shelfLength - 1) {
          if (gondolaIndex < gondolas.length - 1) {
            gondolaIndex = gondolaIndex + 1;
            shelfIndex = 0;
            binIndex = 0;
          }
        } else {
          shelfIndex = shelfIndex + 1;
          binIndex = 0;
        }
      } else {
        binIndex = binIndex + 1;
      }

      const newBin =
        planogram?.result.gondolas[gondolaIndex].shelves[shelfIndex].bins[
          binIndex
        ];

      if (!newBin) return null;

      return {
        ...newBin,
        gondolaIndex,
        shelfIndex,
        binIndex,
      } as ExtendedBin;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const currentBin = selectedBin$.peek();

      if (!currentBin) return;

      let newBin: ExtendedBin | undefined | null;

      if (e.key === "ArrowLeft") {
        e.stopImmediatePropagation();
        e.preventDefault();
        newBin = navigateLeft(currentBin);
      }

      if (e.key === "ArrowRight") {
        e.stopImmediatePropagation();
        e.preventDefault();
        newBin = navigateRight(currentBin);
      }

      if (newBin) {
        const { dimensions } = calcBinDimensions(newBin);

        selectedBin$.set({
          ...newBin,
          dimensions,
        });
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [planogram]);

  const [binGroups, setBinGroups] = useState<BinGroupType[]>([]);

  const getDistinctBinGroups = (gondolas: Gondola[]) => {
    const distincBinGroups = new Map<string, BinGroupType>();

    [...gondolas]
      .sort((a, b) => a.id - b.id)
      .forEach((gondola, gondolaIndex) => {
        gondola.shelves.forEach((shelf, shelfIndex) => {
          shelf.bins.forEach((bin, binIndex) => {
            const { id, dimensions } = calcBinDimensions(bin);

            const newBin: ExtendedBin = {
              ...bin,
              binIndex,
              shelfIndex,
              gondolaIndex,
            };

            if (distincBinGroups.has(id)) {
              const binGroup = distincBinGroups.get(id);

              if (!binGroup) return;
              const { dimensions, bins = [] } = binGroup;

              distincBinGroups.set(id, {
                dimensions,
                bins: [...bins, newBin],
              });
            } else {
              distincBinGroups.set(id, {
                dimensions,
                bins: [newBin],
              });
            }
          });
        });
      }, []);

    return Array.from(distincBinGroups.values());
  };

  useEffect(() => {
    if (!planogram) return;

    const binGroups = getDistinctBinGroups(planogram.result.gondolas);

    setBinGroups(binGroups);
  }, [planogram]);

  if (!isVisible) return null;

  return (
    <>
      {binGroups.map(
        (binGroup, i) =>
          binGroup.bins.length > 0 &&
          binGroup.dimensions && (
            <BinGroup
              index={i}
              selected$={selectedBin$}
              hovered$={hovered$}
              binGroup={binGroup}
              key={i}
            />
          )
      )}
      <SelectedBin selected$={selectedBin$} binGroups={binGroups} />
    </>
  );
}
